import { CareerType, CountryType, DividerType, FormationType, ProjectElementType, TextAlignment, DisplayAlignment, DisplaySize, WorkType, SupportStatus, SupportType, CareerPeriodType, SizeUnit, getSizeUnitDisplayValue, BillingFieldState, SortDirection, WorkSortOrder, SupportSortOrder, MemberSortOrder, VenueSortOrder, CurrencyCode, SystemAlertType, PollQuestionType, CoverHeight, OnBoardingStepType, PasswordStatus, ExposureLevel, NavLayout, TextBlockType, TextSortOrder, PerPage, DimensionsType, Language, SubscriptionStatus, DetailType, ProjectType, ProjectInfoType, MemberType, GroupMemberRole, GroupMemberStatus, ArchiveRole, SignupArchiveType, FormationHorizontalAlignment, FormationImageMode, FormationVerticalAlignment, PEInfoType, WorkListType, VenueType, VenueLifecycleStatus, VenueAdmissionType, DayOfWeek } from '../enum/EnumRepository';

/**
 * 전부 ~Data로 통일
 */

export interface UserData {
  id: string;
  email: string;
  username: string;
  aui: string;
  handle: string;
  role: string;
  subscriptionStatus: string;
  passwordStatus: PasswordStatus;
  // landingGuide?: boolean;
}

export interface UserDataWithRefreshToken extends UserData {
  accessToken: string;
  // landingGuide: boolean;
}

export interface UploadFileData {
  id: string;
  originUrl: string;
  allowDownload: boolean;
  width: number;
  height: number;
}

export interface MemberSearchData {
  username: string;
  aui: string;
  handle: string;
  /** 연결 시 사용하는 resolved 이름(MemberInfo → SEO → username fallback). */
  name?: string;
  nameEn?: string;
  /** Artist 연결 검색 응답에서 내려오는 선택적 계정 정보. */
  memberType?: MemberType;
  archiveRole?: ArchiveRole | null;
  exposureLevel?: ExposureLevel;
  /** Archive Index에서 Billboard 이미지가 있을 때만 제공됩니다. 검색 응답에서는 생략됩니다. */
  originUrl?: string;
}

export interface PrerenderProfileData {
  aui: string;
  handle: string;
  seo: SeoData;
  memberInfo?: {
    year: string | number | null;
    country: CountryType | null;
  } | null;
}

export interface WorkArtistData {
  name: string;
  nameEn: string;
  linkedMemberAui: string | null;
  linkedMemberHandle: string | null;
}

export interface GroupMemberData {
  id: string;
  name: string;
  nameEn: string;
  role: GroupMemberRole;
  country: CountryType;
  year: number;
  email: string;
  contact: string;
  description: string;
  descriptionEn: string;
  sns: SocialMedia;
  tags: GroupMemberTagData[];
  propertyVisible: GroupMemberPropertyVisibleData;
  status: GroupMemberStatus;
  linkedMemberHandle: string;
  index: number;
  
  hasChanged?: boolean;
}

export interface GroupMemberPropertyVisibleData {
  id: string;
  email: boolean;
  contact: boolean;
  twitter: boolean;
  instagram: boolean;
  facebook: boolean;
  youtube: boolean;
  url1: boolean;
}

export interface GroupMemberLinkPreviewData {
  linkToken: string;
  name: string;
  nameEn: string;
  country: CountryType;
  year: number;
  email: string;
  contact: string;
  description: string;
  descriptionEn: string;
  sns: SocialMedia;
}

export interface BillboardData {
  id: string;
  uploadFile: UploadFileData;
  displaySize: DisplaySize;
}

export interface SizeData {
  width: string;
  height: string;
  depth?: string;
  dpi?: string;
  unit: SizeUnit;
}

export const getDefaultSizeData = (): SizeData => ({
  width: '',
  height: '',
  unit: SizeUnit.CM,
});

export const getMainSizeData = (
  sizes: SizeData[] | undefined,
  mainSizeUnit: SizeUnit | undefined
): SizeData => {
  if (!sizes || sizes.length === 0) return getDefaultSizeData();
  return sizes.find((size) => size.unit === mainSizeUnit) ?? sizes[0];
}

const hasSizeFieldValue = (value: string | number | null | undefined): boolean =>
  value !== undefined && value !== null && value !== '';

export const hasSizeData = (size: SizeData | undefined): boolean =>
  Boolean(size && (
    hasSizeFieldValue(size.width) ||
    hasSizeFieldValue(size.height) ||
    hasSizeFieldValue(size.depth) ||
    hasSizeFieldValue(size.dpi)
  ));

export const isSameSizeData = (
  a: SizeData | undefined,
  b: SizeData | undefined
): boolean =>
  (a?.unit ?? null) === (b?.unit ?? null) &&
  (a?.width ?? '') === (b?.width ?? '') &&
  (a?.height ?? '') === (b?.height ?? '') &&
  (a?.depth ?? '') === (b?.depth ?? '') &&
  (a?.dpi ?? '') === (b?.dpi ?? '');

export const hasWorkSizeData = (sizes: SizeData[] | undefined): boolean =>
  sizes?.some(hasSizeData) ?? false;

export const hasSizeDataForUnit = (
  sizes: SizeData[] | undefined,
  unit: SizeUnit
): boolean =>
  hasSizeData((sizes ?? []).find((size) => size.unit === unit));

export const hasValidMainSizeUnitForUpdate = (
  sizes: SizeData[] | undefined,
  mainSizeUnit: SizeUnit
): boolean => {
  const validSizes = (sizes ?? []).filter(hasSizeData);
  if (validSizes.length === 0) return true;
  return validSizes.some((size) => size.unit === mainSizeUnit);
}

export const normalizeWorkSizesForUpdate = (
  sizes: SizeData[] | undefined,
  mainSizeUnit: SizeUnit
): { sizes: SizeData[]; mainSizeUnit: SizeUnit } => {
  return {
    sizes: (sizes ?? []).filter(hasSizeData),
    mainSizeUnit,
  };
}

export const mergeWorkSizeUpdates = (
  sizes: SizeData[] | undefined,
  sizeUpdates: SizeData[]
): SizeData[] => {
  return sizeUpdates.reduce((merged, update) => {
    if (!hasSizeData(update)) {
      return merged.filter((size) => size.unit !== update.unit);
    }
    const hasUnitSize = merged.some((size) => size.unit === update.unit);
    return hasUnitSize
      ? merged.map((size) => size.unit === update.unit ? update : size)
      : [...merged, update];
  }, [...(sizes ?? [])]);
}

export const getAreaFromSize = (value: SizeData): number => {
  if (value.depth) {
    return Number.parseInt(value.width) * Number.parseInt(value.height) * Number.parseInt(value.depth);
  } else {
    return Number.parseInt(value.width) * Number.parseInt(value.height);
  }
}

export const convertSizeToString = (value: SizeData): string => {
  if (!hasSizeData(value)) return '-';
  const displayUnit = getSizeUnitDisplayValue(value.unit);
  const dpi = value.dpi ? `, ${value.dpi} dpi` : '';
  if (value.depth) {
    return `${value.height ?? '0'}×${value.width ?? '0'}×${value.depth ?? '0'} ${displayUnit}${dpi}`;
  } else {
    return `${value.height ?? '0'}×${value.width ?? '0'} ${displayUnit}${dpi}`;
  }
}

export const convertWorkSizeToString = (
  value: SizeData,
  dimensionsType: DimensionsType,
  sizeText?: string | null,
  variableLabel = 'Dimensions variable',
): string => {
  if (dimensionsType === DimensionsType.VARIABLE) return variableLabel;
  if (dimensionsType === DimensionsType.CUSTOM) return sizeText?.trim() || '-';
  return convertSizeToString(value);
}

export interface WorkData {
  id: string;
  workType: WorkType;
  // video일 경우 썸네일, 고정 이미지일 경우 원본 이미지
  uploadFile: UploadFileData;
  title: string;
  titleEn: string;
  description: string;
  descriptionEn: string;
  material: string,
  materialEn: string;
  prodYear: string,
  price: Money,
  isPOV: boolean,
  collection: string
  
  // video related
  videoUrl: string;
  duration: string;
  
  mainSizeUnit: SizeUnit;
  sizes: SizeData[];
  sizeText: string;
  dimensionsType: DimensionsType;
  tags: WorkTagData[];
  credit: MemberCreditData | null;
  artist: WorkArtistData | null;
  isPublic: boolean;
}

// 프론트 표시/편집용: full WorkData.sizes 중 mainSizeUnit에 해당하는 대표 size를 붙인 형태.
export interface PEWorkData {
  id: string;
  workType: WorkType;
  // video일 경우 썸네일, 고정 이미지일 경우 원본 이미지
  uploadFile: UploadFileData;
  title: string;
  titleEn: string;
  description: string;
  descriptionEn: string;
  size: SizeData;
  sizeText: string;
  dimensionsType: DimensionsType;
  material: string,
  materialEn: string;
  prodYear: string,
  
  // video related
  videoUrl: string;
  duration: string;
  artist: WorkArtistData | null;
}

/**
 * WorkTag, GroupMemberTag, VenueTag, TextTag API가 공통으로 사용하는 TagDto 응답 형태.
 * 실제 엔티티와 프론트 상태는 각 도메인별로 분리한다.
 */
export interface TagData {
  id: number;
  name: string;
  index: number;
}

export type WorkTagData = TagData;
export type GroupMemberTagData = TagData;
export type VenueTagData = TagData;
export type TextTagData = TagData;

export interface MemberCreditData {
  id: number;
  name: string;
  visible: boolean;
  index: number;
}

export interface Money {
  amount: number;        // 소수 4자리까지
  currency: CurrencyCode;
}

export interface SelectedData {
  workId: string;
  detailId?: string;
  originUrl: string;
  title?: string;
  titleEn?: string;
  prodYear?: string;
  material?: string;
  materialEn?: string;
  size?: SizeData;
  sizeText?: string;
  dimensionsType?: DimensionsType;
  duration?: string;
  price?: Money;
  isPOV?: boolean;
  artist?: WorkArtistData | null;
}

export interface WorkSimpleForExportData {
  id: string;
  title: string;
  originUrl: string;
  workType: WorkType;
  prodYear: string;
  duration: string;
  size: SizeData;
  sizeText: string;
  dimensionsType: DimensionsType;
  artist: WorkArtistData | null;
}
export interface WorkSimpleForImportData {
  id: string;
  title: string;
  titleEn: string;
  // originUrl: string;
  uploadFile: UploadFileData;
  workType: WorkType;
  material: string;
  materialEn: string;
  prodYear: string;
  duration: string;
  size: SizeData;
  sizeText: string;
  dimensionsType: DimensionsType;
  artist: WorkArtistData | null;

  // detail cut이 있는지 없는지
  hasDetail: boolean;
}
export interface WorkDetailSimpleData {
  id: string;
  uploadFile: UploadFileData;
  detailType: DetailType;
  description: string;
}

export interface WorkSimpleWithDetails extends WorkSimpleForImportData {
  detailList: WorkDetailSimpleData[];
}

export interface WorkDetailData {
  id: string;
  workId: string;
  uploadFile: UploadFileData;
  description: string;
  /** IMAGE | VIDEO. VIDEO일 때 videoUrl 사용. */
  detailType: DetailType;
  videoUrl: string;

  hasChanged?: boolean;
  imageChanged?: boolean;
}
export interface WorkWithDetailAndRelatedProjectData extends WorkData {
  workDetailList: WorkDetailData[];
  relatedProjectList: ProjectRelatedData[];
}

export interface WorkPropertyVisibleData {
  workPropertyVisibleId: string;
  workType: boolean;
  imageUrl: boolean;
  description: boolean;
  price: boolean,
  collection: boolean;
  /** API engVisible — Work 영문 노출 (DB engVisibleWork) */
  engVisible: boolean;
  workListType?: WorkListType;
}

/** workType·imageUrl 등 필드별 공개 토글 (engVisible·workListType 제외) */
export type WorkPropertyVisibleToggleField = Exclude<
  keyof WorkPropertyVisibleData,
  'workPropertyVisibleId' | 'engVisible' | 'workListType'
>;

export interface DocumentData {
  id: string;
  uploadFile: UploadFileData;
  description: string;
  /** IMAGE | VIDEO. VIDEO일 때 videoUrl 사용. duration 없음. */
  detailType: DetailType;
  videoUrl: string;
}

export interface PDFAssetData {
  id: string;
  uploadFile: UploadFileData;
  fileSize: number;
  description: string;
}

export interface TextBoxData {
  id: string;
  content: string;
}

export interface ProjectData {
  id: string;
  title: string;
  uploadFile: UploadFileData;
  isPublic: boolean;
  coverHeight: CoverHeight;
  focusX: number;
  focusY: number;
  projectType: ProjectType;
  projectYear: number | null;

  titleChanged?: boolean;
  imageChanged?: boolean;
  focusChanged?: boolean;
}

export interface ProjectInfoValueData {
  text: string;
  url: string | null;
  venueId: number | null;
}

export interface ProjectInfoData {
  id: string;
  type: ProjectInfoType;
  customName: string | null;
  customValues: ProjectInfoValueData[];

  hasChanged?: boolean;
}

// 뭔가 애매하네
export interface ProjectRelatedData {
  id: string;
  title: string;
  isPublic: boolean;
}

export interface ProjectSimpleData {
  id: string;
  title: string;
  originUrl: string;
  focusX: number;
  focusY: number;
  isPublic: boolean;
  projectType: ProjectType;
  projectYear: number | null;
}

interface ProjectElementBase {
  id: string;
  projectElementType: ProjectElementType;

  hasChanged?: boolean;
  imageChanged?: boolean;
}

export interface ProjectElementDataWithWork extends ProjectElementBase {
  projectElementType: ProjectElementType.WORK;
  work: PEWorkData;
  displayAlignment: DisplayAlignment;
  displaySize: DisplaySize;
  infoType?: PEInfoType;
}

export interface ProjectElementDataWithDetail extends ProjectElementBase {
  projectElementType: ProjectElementType.DETAIL;
  workDetail: WorkDetailData;
  displayAlignment: DisplayAlignment;
  displaySize: DisplaySize;
  infoType?: PEInfoType;
}

export interface ProjectElementDataWithDocument extends ProjectElementBase {
  projectElementType: ProjectElementType.DOCUMENT;
  document: DocumentData;
  displayAlignment: DisplayAlignment;
  displaySize: DisplaySize;
  infoType?: PEInfoType;
}

export interface ProjectElementDataWithPDF extends ProjectElementBase {
  projectElementType: ProjectElementType.PDF;
  pdfAsset: PDFAssetData;
  displayAlignment: DisplayAlignment;
}

export interface ProjectElementDataWithText extends ProjectElementBase {
  projectElementType: ProjectElementType.TEXT;
  text: TextData;
  selectedTextBlocks: TextBlockData[];
  displayAlignment: DisplayAlignment;
  displaySize: DisplaySize;
}

export interface ProjectElementDataWithTextBox extends ProjectElementBase {
  projectElementType: ProjectElementType.TEXTBOX;
  textBox: TextBoxData;
  textAlignment: TextAlignment;
}

export interface ProjectElementDataWithDivider extends ProjectElementBase {
  projectElementType: ProjectElementType.DIVIDER;
  dividerType: DividerType;
}

export interface FormationItemData {
  id: string;
  slotIndex: number;
  work: PEWorkData | null;
  workDetail: WorkDetailData | null;
  uploadFile: UploadFileData;
  /** 직접 업로드 이미지에만 사용하는 한 줄 정보. */
  caption?: string;
  focusX: number;
  focusY: number;
  /** 구형 데이터에는 없으므로 undefined는 기존 동작인 FILL로 해석한다. */
  imageMode?: FormationImageMode;
  horizontalAlignment?: FormationHorizontalAlignment;
  verticalAlignment?: FormationVerticalAlignment;
  imageChanged?: boolean;
  captionChanged?: boolean;
  focusChanged?: boolean;
  referenceChanged?: boolean;
  presentationChanged?: boolean;
}

export interface FormationData {
  id: string;
  formationType: FormationType;
  items: FormationItemData[];
}

export interface ProjectElementDataWithFormation extends ProjectElementBase {
  projectElementType: ProjectElementType.FORMATION;
  formation: FormationData;
  displaySize: DisplaySize;
  infoType?: PEInfoType;
}

export type ProjectElementData =
  | ProjectElementDataWithWork
  | ProjectElementDataWithDetail
  | ProjectElementDataWithText
  | ProjectElementDataWithTextBox
  | ProjectElementDataWithDocument
  | ProjectElementDataWithPDF
  | ProjectElementDataWithDivider
  | ProjectElementDataWithFormation;


export interface MemberInfoData {
  id: string;
  uploadFile: UploadFileData;
  name: string;
  nameEn: string;
  year: string;
  country: CountryType;
  email: string;
  contact: string;
  description: string;
  descriptionEn: string;
  engVisible: boolean;
}

export interface ContactData {
  id: string;
  address: string;
  email: string;
  contact: string;
  sns: SocialMedia;
}

export interface SocialMedia {
  twitter: string;
  instagram: string;
  facebook: string;
  threads: string;
  behance: string;
  youtube: string;
  vimeo: string;
  url1: string;
}

export interface ContactPropertyVisibleData {
  id: string;
  address: boolean;
  email: boolean;
  contact: boolean;
  twitter: boolean;
  instagram: boolean;
  facebook: boolean;
  youtube: boolean;
  url1: boolean;
}

interface CareerBase {
  id: string;
  careerType: CareerType;
  content: string;
  contentEn: string;
  relatedUrl: string;
  isPublic: boolean;

  hasChanged?: boolean;
}

export interface CareerPointData extends CareerBase {
  periodType: CareerPeriodType.POINT;
  startYear: string;
  startMonth: string;
  startDay: string;
}

export interface CareerPeriodData extends CareerBase {
  periodType: CareerPeriodType.PERIOD;
  isCurrent: boolean;
  currentLabelOrig: string;
  startYear: string;
  startMonth: string;
  startDay: string;
  endYear: string;
  endMonth: string;
  endDay: string;
}

export type CareerData =
  | CareerPointData
  | CareerPeriodData;

export interface AboutPageSettingData {
  careerSectionOrder: CareerType[];
}

export interface SettingData {
  id: string;
  username: string;
  exposureLevel: ExposureLevel;
  menuVisible: MenuVisible;
  navLayout: NavLayout;
  seo: SeoData;
  
  aui: string;
  handle: string;
  memberType: MemberType;
  archiveRole: ArchiveRole | null;
  marketingEmail: boolean;
}

export interface MenuVisible {
  billboard: boolean;
  projects: boolean;
  works: boolean;
  texts: boolean;
  members: boolean;
  about: boolean;
  contact: boolean;
}

export interface SeoData {
  subTitle: string;
  uploadFile: UploadFileData;
  name: string;
  nameEn: string;
  description: string;
  descriptionEn: string;

  location: string;
  websiteUrl: string;
  instagramUrl: string;
}

export interface GuideVisit {
  landingGuide?: boolean;
  generalGuide?: boolean;
}

// Legacy ImageMetadata (이전 구조)
export interface ImageMetadataV1 {
  MOBILE: boolean;
  TABLET: boolean;
  LAPTOP: boolean;
  DESKTOP: boolean;
  origin: boolean;
}

// New ImageMetadata (새로운 구조)
export interface ImageMetadataV2 {
  origin_ext: string;
  resized_ext: string;
  available: {
    THUMBNAIL: boolean;
    MOBILE: boolean;
    TABLET: boolean;
    LAPTOP: boolean;
    DESKTOP: boolean;
    origin: boolean;
  };
}

// Union type으로 두 구조 모두 지원
export type ImageMetadata = ImageMetadataV1 | ImageMetadataV2;

export type IndexOrderOriginUrl = string | { src: string };

export interface IndexOrderData {
  index: number;
  id: string;
  mainText: string;
  subText: string;
  originUrl?: IndexOrderOriginUrl;
}
export interface IndexOrderWorkData {
  index: number;
  id: string;
  title: string;
  originUrl: string;
  artist: WorkArtistData | null;
}

export interface IndexOrderTextData {
  index: number;
  id: string;
  title: string;
}

export interface PageData {
  page: number;
  size: PerPage;
  totalElements: number;
  totalPages: number;
}

/**
 * GET /api/v1/venue/list — page는 1부터, size 기본 20·최대 100.
 * 기존 PageData.size(PerPage)와 달라 숫자 size를 쓴다.
 */
export interface VenuePageData {
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
  content: SimpleVenueData[];
}

/**
 * 운영시간. 요일 중복 불가, 최대 7개.
 * 열면 opensAt < closesAt 둘 다 필수, 휴무면 시간 둘 다 null.
 */
export type VenueOpeningHoursData =
  | {
      dayOfWeek: DayOfWeek;
      closed: false;
      opensAt: string;
      closesAt: string;
    }
  | {
      dayOfWeek: DayOfWeek;
      closed: true;
      opensAt: null;
      closesAt: null;
    };

export interface SimpleVenueData {
  id: string;
  /** 생성 후 변경 불가. 서버 trim 후 대문자 저장, 응답 그대로 표시 */
  venueCode: string;
  name: string;
  nameEn: string;
  type: VenueType;

  /** 위도·경도는 둘 다 있거나 둘 다 없음 */
  latitude: number | null;
  longitude: number | null;
  admissionType: VenueAdmissionType;
  city: string;
  district: string;
  instagramUrl: string;
}

export interface VenueData {
  id: string;
  /** 생성 후 변경 불가. 서버 trim 후 대문자 저장, 응답 그대로 표시 */
  venueCode: string;
  name: string;
  nameEn: string;
  type: VenueType;
  lifecycleStatus: VenueLifecycleStatus;
  /** ISO 3166-1 alpha-2. 요청은 KR/kr 모두 가능, 저장·응답은 대문자 */
  countryCode: string;
  city: string;
  district: string;
  roadAddress: string;
  roadAddressEn: string;
  /** 위도·경도는 둘 다 있거나 둘 다 없음 */
  latitude: number | null;
  longitude: number | null;
  admissionType: VenueAdmissionType;
  /** IANA, e.g. Asia/Seoul */
  timeZone: string;
  instagramUrl: string;
  websiteUrl: string;
  phoneNumber: string;
  description: string;
  descriptionEn: string;
  naverMapUrl: string;
  googleMapUrl: string;
  admissionNote: string;
  openingHoursNote: string;
  openingHours: VenueOpeningHoursData[];
  tags: VenueTagData[];
}

export type VenueBulkResultStatus =
  | 'CREATED'
  | 'SKIPPED_DUPLICATE'
  | 'CONFLICT'
  | 'INVALID';

export interface CreateVenueBulkItemResult {
  rowNumber: number;
  venueCode: string | null;
  status: VenueBulkResultStatus;
  venueId: number | null;
  message: string | null;
}

export interface CreateVenueBulkResult {
  totalCount: number;
  createdCount: number;
  duplicateCount: number;
  conflictCount: number;
  invalidCount: number;
  results: CreateVenueBulkItemResult[];
}

export interface VenueSortData {
  sort: VenueSortOrder;
  direction: SortDirection;
}

export interface VenueFilterData {
  venueType: {
    [key in VenueType]: boolean;
  };
  /** true면 FREE만. false면 전체 */
  admissionFree: boolean;
  /** true일 때만 openToday=true */
  openToday: boolean;
  /** 선택한 태그 중 하나라도 연결된 Venue만(OR). 쿼리: tagIds=1&tagIds=2 */
  tagIds: number[];
  /** 지정한 district 중 하나와 일치(OR, 정확 일치). 쿼리: districts=종로구&districts=강남구. 비우면 미전송 */
  districts: string[];
}

export interface WorkSortData {
  sort: WorkSortOrder;
  direction: SortDirection;
}

export interface WorkFilterData {
  workType: {
    [key in WorkType]: boolean;
  };
  prodYear: {
    min: number | null;
    max: number | null;
  };
  /** 백엔드 WorkFilterParam.tagIds: 지정한 태그 id를 모두 가진 작품만(AND). 쿼리: tagIds=1&tagIds=2 */
  tagIds: number[];
  /** 선택한 MemberCredit 중 하나가 지정된 작품만(OR). */
  creditIds: number[];
}

export interface SupportData {
  id: string;
  aui: string;
  type: SupportType;
  title: string;
  message: string;
  createdAt: string;
  status: SupportStatus;
}

export interface SupportSortData {
  sort: SupportSortOrder;
  direction: SortDirection;
}

// Payment - polar
export interface ProductData {
  id: string
  name: string
  description: string
  imageUrl: string                 // 대표 이미지. 없으면 undefined
  prices: ProductPrice[]            // 여러 가격: 보통 1개지만 배열로 온다
  archived: boolean                 // 대시보드에서 Archived 처리 여부
  medias: Media[]                  // 전체 이미지(커버 + 추가 컷)
}

export interface ProductPrice {
  id: string
  price_amount: number                    // 금액: 통화의 최소 단위 (KRW-원, USD-센트)
  price_currency: string                  // ISO-4217, e.g. "KRW", "USD"
  recurring_interval: string                  // "ONE_TIME" | "MONTHLY" | "YEARLY" …
  type: string               // "FIXED_PRICE" | "PAY_WHAT_YOU_WANT" | "FREE"
}

export interface Media {
  id: string
  public_url: string                 // CDN URL (nullable on backend → make optional?)
  mimeType: string                  // 파일 타입
  size: number                      // 파일 크기 (bytes) 
}

export interface PlanData {
  name: string;
  status: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  canceledAt: string;
  endedAt: string;
  storage: StorageData;
}

export interface StorageData {
  usedSize: number;
  maxSize: number;
}

export interface CustomerPortalUrlData {
  url: string;
  expiresAt: number;
}

export interface CheckoutData {
  id: string;                 // polar checkout id
  url: string;                // checkout page url
  status: string;             // open / completed …
}

//   Admin
export interface MemberData {
  memberId: string;
  aui: string;
  username: string;
  memberType: MemberType;
  handle: string;
  lastLoginAt: string;
  exposureLevel: ExposureLevel;
  memberStatus: string;
  discount: string;
  discountHistory: DiscountSimpleData[];
  status: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  storage: StorageData;
}

export interface MemberSortData {
  sort: MemberSortOrder;
  direction: SortDirection;
}

export interface MemberFilterData {
  subscription: {
    [key in SubscriptionStatus]: boolean;
  };
  /** 노출 단계 필터 — `Setting.exposureLevel` 과 동일 (PUBLIC / SEARCHABLE / PRIVATE) */
  isPublic: Record<ExposureLevel, boolean>;
  lastLoginAt: {
    min: string | null;
    max: string | null;
  };
}

export interface OverallData {
  memberCount: number;
  subscriptionCount: number;
  storageOverall: StorageOverallData;
}
export interface StorageOverallData {
  totalOriginBytes: number;
  totalBytes: number;
  totalFiles: number;
}
export interface SystemAlertData {
  id: string;
  type: SystemAlertType;
  message: string;
  createdAt: string;
  scheduledStartAt: string;
  scheduledEndAt: string;
  endedAt: string;  // = actualEndDate
}
export interface DiscountSimpleData {
  name: string;
  amount: number;
  currency: string;
  paidAt: string;
}


// ============================== Poll ==============================

export interface QuestionOption {
  value: string;
  label: string;
  hasOther?: boolean;
  otherMaxLength?: number;
}

export interface PollQuestion {
  id: string;
  title: string;
  type: PollQuestionType;
  options?: QuestionOption[];
}

export interface PollSelectedValue {
  option: string;
  value: string;
}
export interface PreSignedData {
  id: string;
  preSignedUrl: string;
  key: string;
}

// ============================== OnBoarding ==============================
export interface OnBoardingStep {
  step: OnBoardingStepType;
  titleKey: string;
}

export interface OnBoardingStepState {
  steps: OnBoardingStep[];
  currentStep: OnBoardingStep;
  maxReachedIndex: number;
}

export interface OnBoardingData {
  email: string;
  verifiedEmail: boolean;
  handle: string;
  verifiedHandle: boolean;
  signupArchiveType: SignupArchiveType | null;
  works: OnBoardingWorkData[];
  isPreviewGuideMode: boolean;
  isAgreePolicy: boolean;
}
// export interface OnBoardingData {
//   email: string;
//   verifiedEmail: boolean;
//   handle: string;
//   verifiedHandle: boolean;
//   projectCover: { file: File, width: number, height: number } | null;
//   projectInfo: OnBoardingProjectInfoData;
//   projectElements: OnBoardingElementData[];
//   isPreviewGuideMode: boolean;
//   isAgreePolicy: boolean;
// }

// export interface OnBoardingProjectInfoData {
//   title: string;
//   activeDate: string;
//   location: string;
//   collaborators: string;
//   support: string;
//   keywords: string;
// }

export interface OnBoardingWorkData {
  file: File;
  width: number;
  height: number;
  title: string;
  size: SizeData;
  dimensionsType: DimensionsType;
  material: string;
  prodYear: string;
}

// export interface OnBoardingElementData {
//   projectElementType: ProjectElementType.WORK;
//   data: OnBoardingWorkData;
//   displayAlignment?: DisplayAlignment;
//   displaySize?: DisplaySize;
//   textAlignment?: TextAlignment;
// }

export interface PageOwnerIdentity {
  aui: string;
  handle: string;
};

// ============================== Text ==============================
export interface TextData {
  id: string;
  source: TextSourceData;
  title: string;
  subtitle: string;

  isPublic: boolean;
  prodYear: string;
  /**
   * 상세/생성/수정/bulk/reorder/toggle 응답에만 포함.
   * 목록·ProjectElement text는 필드 자체가 생략된다.
   */
  tags?: TextTagData[];
}

export interface TextSourceData {
  author: string;
  title?: string;
}

export interface TextWithTextBlockData extends TextData {
  textBlockList: TextBlockData[];
}

export interface TextWithTextBlockAndRelatedProjectData extends TextData {
  textBlockList: TextBlockData[];
  relatedProjectList: ProjectRelatedData[];
}

export type TextBlockData =
  | TextHeadingBlockData
  | TextParagraphBlockData
  | TextImageBlockData
  | TextQuoteBlockData
  | TextSourceLineBlockData
  | TextLinkBlockData;

export interface TextBlockBaseData {
  id: string;
  type: TextBlockType;
  /** LINK href. 다른 타입은 서버가 비움. 최대 2000자 */
  url?: string;

  hasChanged?: boolean;
  imageChanged?: boolean;
}

export interface TextHeadingBlockData extends TextBlockBaseData {
  type: TextBlockType.HEADING;
  textContent: string;
}
export interface TextParagraphBlockData extends TextBlockBaseData {
  type: TextBlockType.PARAGRAPH;
  textContent: string;
}
export interface TextImageBlockData extends TextBlockBaseData {
  type: TextBlockType.IMAGE;
  /** 이미지 미추가 상태에서는 서버/클라 모두에서 생략될 수 있음 */
  uploadFile: UploadFileData;
  caption: string;
}
export interface TextQuoteBlockData extends TextBlockBaseData {
  type: TextBlockType.QUOTE;
  textContent: string;
}
export interface TextSourceLineBlockData extends TextBlockBaseData {
  type: TextBlockType.SOURCE_LINE;
  textContent: string;
}

export interface TextLinkBlockData extends TextBlockBaseData {
  type: TextBlockType.LINK;
  /** 링크 라벨. 빈 블록이면 생략되거나 '' */
  textContent: string;
}



export interface TextSimpleData {
  id: string;
  title: string;
  author: string;
}

export interface MaterialAliasSetData {
  id: string;
  material: string;
  materialEn: string;
}

export interface MaterialAliasData {
  id: string;
  keyword: string;
  frequency: number;
  aliasSet: MaterialAliasSetData | null;
}

export interface TextSortData {
  sort: TextSortOrder;
  direction: SortDirection;
}

export interface TextFilterData {
  /** 지정한 태그 중 하나라도 연결된 Text만(OR). 쿼리: tagIds=1&tagIds=2 */
  tagIds: number[];
}

/**
 * 다국어 메시지 단일 항목 (alertMessages, tempAlertMessages, btnMessages 공통)
 */
export interface LocalizedMessage {
  [Language.EN]: string;
  [Language.KR]: string;
  // [Language.JA]?: string;
  // [Language.ZH]?: string;
}

/**
 * 키별 다국어 메시지 맵 (AlertMessageKey, TempAlertMessageKey, BtnMessageKey 등에 사용)
 */
export type LocalizedMessages<Key extends string> = {
  [key in Key]: LocalizedMessage;
};
